import { RowData } from "@tanstack/react-table"
import { Property } from "csstype"
import { SelectOption } from "../../../common/types"
import { CUSTOM_FIELD_AVAILABLE_FROM } from "../../../utils/customField"
import { Message, Messages } from "../../helpers/IntlProps"
import { NormalizedLocation } from "../../inventory/location/reducers/types"
import { BulkActionDefinition, ColumnDefinition } from "./column-definitions"

/**
 * The select column is declared as a variable and therefore only has visibility on the
 * initial state and props values. We extend the table's meta object to define custom attributes
 * in the table's context that represent our state variables since headers are refreshed
 * based on the table's context. This is the state-safe extension of the meta interface.
 */
declare module "@tanstack/react-table" {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    interface TableMeta<TData extends RowData> {
        isAllPagesSelected: boolean
        totalItemsCount: number
        additionalOptions: {[key: string]: SelectOption<string>[]}
        inventoryLocations: NormalizedLocation[]
        filters?: FiltersState
    }

    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    interface ColumnMeta<TData extends RowData, TValue> {
        align: Property.TextAlign
        sortable: boolean
        columnDefinition?: ColumnDefinition
    }
}

export enum ObjectType {
    INVOICES = "invoices",
    CREDIT_MEMOS = "credit-memos",
    QUOTES = "quotes",
    ORDERS = "orders",
    PAYMENTS = "payments",
    CONTACTS = "contacts",
    COMPANIES = "companies",
    PRODUCTS = "products",
    MATRICES = "matrices",
    BUNDLES = "bundles",
    SUBSCRIPTIONS = "subscription",
    RETURNS = "return",
    SUPPLIERS = "suppliers",
    PRICE_LISTS = "price-lists",
    ITEM_REPORT = "itemReportList",
    ORDER_LINES = "order-lines",
    CALENDAR_EVENTS = "calendar-events",
    SCHEDULING_PAGES = "scheduling-pages",
    INVENTORY_STOCK_DOCUMENTS = "inventory-stock-documents",
    ACCOUNT_STATEMENTS = "account-statements"
}

export const CUSTOM_FIELD_AVAILABLE_LISTINGS: {[key in ObjectType]?: string} = {
    [ObjectType.INVOICES]: CUSTOM_FIELD_AVAILABLE_FROM.INVOICE,
    [ObjectType.RETURNS]: CUSTOM_FIELD_AVAILABLE_FROM.RETURN,
    [ObjectType.QUOTES]: CUSTOM_FIELD_AVAILABLE_FROM.QUOTE,
    [ObjectType.ORDERS]: CUSTOM_FIELD_AVAILABLE_FROM.ORDER,
    [ObjectType.CONTACTS]: CUSTOM_FIELD_AVAILABLE_FROM.PERSON,
    [ObjectType.COMPANIES]: CUSTOM_FIELD_AVAILABLE_FROM.ORGANIZATIONS,
    [ObjectType.SUPPLIERS]: CUSTOM_FIELD_AVAILABLE_FROM.SUPPLIER,
}

export type ObjectTypeConfig = {
    objectType: ObjectType
    columnDefinitions: ColumnDefinition[]
    headerMessages: Messages
    customFieldHeaders?: Messages
    listingPreferencesKey: string
    searchPlaceholder: Message
    displayString: Message
    searchObjectType: ObjectType
    typeOfSearch?: string
    bulkActionDefinitions: BulkActionDefinition[]
    /* eslint-disable @typescript-eslint/no-explicit-any */
    statBoxComponent?: any
    exportModalComponent?: any
    creationButtonComponent?: any
    /* eslint-enable @typescript-eslint/no-explicit-any */
    additionalFilters?: {[key: string]: string[] | string}
}

export type SortingState = {
    sortField: string
    asc: boolean
    nestedSortFilters?: {[key: string]: string}
}

export type FiltersState = {
    renderedKeywords: string
    keywords: string
    renderedFilters: object
    filters: { [key: string]: string | Array<string> | object | boolean }
    savedFilterId: string
    fields: string[]
    totals: string[]
    lightResult: boolean
    view: View
}

export const FILTERS_DEFAULTS: FiltersState = {
    renderedKeywords: "",
    keywords: "",
    renderedFilters: {},
    filters: {},
    savedFilterId: "",
    fields: [],
    totals: [],
    lightResult: true,
    view: {
        columns: []
    }
}

export type PaginationState = {
    activePage: number
    pageSize: number
    pageSizeMax: number
    totalItemCount: number
}

export const PAGINATION_DEFAULTS: PaginationState = {
    activePage: 1,
    pageSize: 10,
    pageSizeMax: 50,
    totalItemCount: 0,
}

export const SORTING_DEFAULTS: SortingState = {
    sortField: "",
    asc: false,
}

export enum StatBoxType {
    SUCCESS = "success",
    WARNING = "warning",
    DANGER = "danger",
}

export type StatBoxDefinition = {
    id: string
    label: Message
    countKeys: string[]
    amountKeys: string[]
    type?: StatBoxType
    filters: StatBoxFilters
}

export type StatBoxFilter = {
    nameId: string
    value: string[] | { startDate: Date, endDate?: Date }
    op: string
    label: string
}

export type StatBoxFilters = {[key: string]: StatBoxFilter}

export type View = {
    columns: ViewColumn[]
}

export type ViewColumn = {
    attributeId: string
    showTotal?: boolean
}

export const JUSTIFY_CONTENT_MAP = {
    "right": "flex-end",
    "left": "flex-start",
    "center": "center",
}


// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/index.ts